import { useState, useEffect } from "react";
import { logger } from "../utils/logger";
import { logoutUser } from "../../customer/api/user";
import { clearAuthData } from "../utils/authUtils";

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if token exists in localStorage on initial load
    const token = localStorage.getItem("token");
    return !!token;
  });
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('user_info');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [roles, setRoles] = useState(() => {
    const info = (() => {
      try { const raw = localStorage.getItem('user_info'); return raw ? JSON.parse(raw) : null; } catch { return null; }
    })();
    const r = Array.isArray(info?.roles) ? info.roles : [];
    return r.map((x) => String(x).toUpperCase());
  });

  // Function to handle login
  const login = (token, user) => {
    if (token) {
      // Normalize: strip any leading 'Bearer ' to avoid 'Bearer Bearer <jwt>' later
      const normalized = String(token).startsWith('Bearer ') ? String(token).slice(7) : String(token);
      localStorage.setItem("token", normalized);
    }
    if (user) {
      const userInfo = {
        id: user?.id ?? null,
        username: user?.username ?? null,
        roles: Array.isArray(user?.roles) ? user.roles : [],
      };
      localStorage.setItem('user_info', JSON.stringify(userInfo));
      if (userInfo.username) {
        localStorage.setItem('username', userInfo.username);
      }
      setUser(userInfo);
      setRoles(userInfo.roles.map((x) => String(x).toUpperCase()));
      
      // Set user context in logger
      logger.setUser(userInfo.id, userInfo.username);
      logger.info('User logged in successfully', { 
        userId: userInfo.id, 
        roles: userInfo.roles 
      });
    }
    setIsAuthenticated(true);
    // Notify listeners in same and other tabs
    window.dispatchEvent(new Event('storage'));
    window.dispatchEvent(new Event('authChange'));
  };

  // Function to handle logout
  const logout = async () => {
    logger.info('User logging out');
    
    try {
      // Call logout API to update backend metrics
      const result = await logoutUser();
      if (result.ok) {
        logger.info('Logout API call successful');
      } else {
        logger.warn('Logout API call failed, proceeding with local logout', { error: result.error, code: result.code });
      }
    } catch (error) {
      // Log error but don't prevent local logout
      logger.warn('Logout API call failed, proceeding with local logout', error);
    }
    
    // Use centralized auth data clearing
    clearAuthData();
    
    // Update local state after clearing auth data
    setIsAuthenticated(false);
    setUser(null);
    setRoles([]);
  };

  // Listen for storage events from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token") {
        setIsAuthenticated(!!e.newValue);
      }
      if (e.key === 'user_info') {
        try {
          const raw = localStorage.getItem('user_info');
          const info = raw ? JSON.parse(raw) : null;
          setUser(info);
          const r = Array.isArray(info?.roles) ? info.roles : [];
          setRoles(r.map((x) => String(x).toUpperCase()));
        } catch {
          setUser(null);
          setRoles([]);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    
    // Custom event for same-tab communication
    const handleAuthChange = () => {
      const token = localStorage.getItem('token');
      const hasToken = !!token;
      setIsAuthenticated(hasToken);
      
      if (hasToken) {
        try {
          const raw = localStorage.getItem('user_info');
          const info = raw ? JSON.parse(raw) : null;
          setUser(info);
          const r = Array.isArray(info?.roles) ? info.roles : [];
          setRoles(r.map((x) => String(x).toUpperCase()));
        } catch {
          setUser(null);
          setRoles([]);
        }
      } else {
        // Auth data was cleared (by forceLogout or clearAuthData)
        setUser(null);
        setRoles([]);
      }
    };
    
    window.addEventListener("authChange", handleAuthChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("authChange", handleAuthChange);
    };
  }, []);

  // Get the current token
  const getToken = () => localStorage.getItem("token");

  // Simplified helpers based on state
  const getUser = () => user;

  const getUserRoles = () => {
    return roles;
  };

  const hasRole = (role) => {
    if (!role) return true;
    const r = String(role).toUpperCase();
    return roles.includes(r) || roles.includes(`ROLE_${r}`);
  };

  const hasAnyRole = (required = []) => {
    if (!required || required.length === 0) return true;
    const set = new Set(roles);
    return required.some((rr) => {
      const r = String(rr).toUpperCase();
      return set.has(r) || set.has(`ROLE_${r}`);
    });
  };

  return { isAuthenticated, login, logout, getToken, getUser, getUserRoles, hasRole, hasAnyRole };
}
